import { memo, useState } from 'react';

import { Popover } from '@headlessui/react';
import Tippy from '@tippyjs/react';
import cs from 'classnames';
import { useRecoilValue } from 'recoil';

import { useClinicalNoteContext } from 'lib/context/ClinicalNoteContext/ClinicalNoteContext';
import { ia, io, pm } from 'lib/helpers/utility';

import Icon from 'components/shared/Icon/Icon';
import Loader from 'components/shared/Loader/Loader';
import { usePopoverContext } from 'components/shared/Popovers/Popover/context/PopoverContext';
import Switch from 'components/shared/Switch/Switch';
import state from 'components/state';

import CreateMacro from '../Sidebar/Macros/UpsertMacro/UpsertMacro';

const Options = ({
  handleUpdate = () => {},
  parentKey = null,
  childKey = null,
  sectionRef = null,
  advanced,
  options
}) => {
  const popoverContext = usePopoverContext();
  const { cnDisplaySettings } = useClinicalNoteContext();
  const sectionValue = cnDisplaySettings?.sections?.[parentKey]?.[childKey]?.advancedForm;
  const [newFormMacro, setNewFormMacro] = useState(null);
  const permissions = useRecoilValue(state.permissions);

  return (
    <>
      <Popover className="relative">
        {({ open }) => (
          <div>
            <Popover.Button
              data-qa="option-button"
              className={cs(
                'flex !h-6 !w-6 cursor-pointer items-center justify-center rounded-[3px] bg-transparent p-0 hover:!bg-primary-100',
                open && '!bg-primary-700 hover:!bg-primary-700'
              )}>
              <Icon
                icon="candle-mono"
                color={open ? 'white' : 'primary'}
                className="cursor-pointer"
              />
            </Popover.Button>
            <Popover.Panel className="absolute right-0 top-full z-[90] mt-[6px] grid w-max cursor-pointer gap-y-1 rounded-lg bg-white !py-2 shadow-medium">
              <div>
                {parentKey && advanced && (
                  <div className="flex justify-between gap-x-[9px] py-[6px] !pl-4 pr-[14px] transition-all hover:bg-primary-50">
                    <span className="text-sm font-400 leading-4 text-primary-900">
                      Advanced template
                    </span>
                    <Switch
                      onChange={(e) => handleUpdate(e, childKey, parentKey)}
                      checked={sectionValue}
                    />
                  </div>
                )}
                <Tippy
                  className="tippy-dark"
                  content="Macro creation for this section is not yet supported."
                  disabled={sectionRef?.current?.formData}>
                  <div
                    data-qa="create-form-macro"
                    onClick={() =>
                      sectionRef?.current && setNewFormMacro(sectionRef?.current?.formData)
                    }
                    className="flex gap-x-[9px] py-[6px] !pl-4 pr-[14px] transition-all hover:bg-primary-50">
                    <Icon icon="save-mono" color="primary" shade={900} className="cursor-pointer" />
                    <span className="text-sm font-400 leading-4 text-primary-900">
                      Create Form Macro
                    </span>
                  </div>
                </Tippy>
                {ia(options) &&
                  options.map(({ title, icon, onClick = () => {}, loading, permission }, index) => {
                    if (permission?.requiredPermissions) {
                      const hasPermission = permission?.isAny
                        ? pm(permissions, permission.requiredPermissions, 'or')
                        : pm(permissions, permission.requiredPermissions);

                      if (!hasPermission) return null;
                    }

                    return (
                      <div
                        onClick={() => !loading && onClick(popoverContext?.context)}
                        key={index}
                        className="flex gap-x-[9px] py-[6px] !pl-4 pr-[14px] transition-all hover:bg-primary-50">
                        {loading ? (
                          <Loader outlined className="text-primary-900" />
                        ) : (
                          <Icon icon={icon} className="cursor-pointer" />
                        )}
                        <span className="text-sm font-400 leading-4 text-primary-900">{title}</span>
                      </div>
                    );
                  })}
              </div>
            </Popover.Panel>
          </div>
        )}
      </Popover>

      {io(newFormMacro) && (
        <CreateMacro
          macroType="form_macro"
          open={!!newFormMacro}
          handleClose={() => setNewFormMacro(null)}
          macroTitle="Form Macro"
          customMacroBody={newFormMacro}
        />
      )}
    </>
  );
};

export default memo(Options);
